/**
 * GRIDHEIMR — Bot List Component
 *
 * Displays a grid of bot cards with real-time updates.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useBots } from "../hooks/useBots";
import { BotCard } from "./BotCard";
import { startBot, stopBot, restartBot, deleteBot } from "../api/bots";
import { getErrorMessage } from "../api/client";
import { useWebSocket, applyBotUpdate } from "../hooks/useWebSocket";
import type { Bot } from "../api/types";
import { BLUE, YELLOW, PIXEL, MONO } from "../theme";

/**
 * Bot Card with WebSocket Updates
 *
 * Wraps BotCard with WebSocket connection for real-time updates.
 */
function BotCardWithWebSocket({
  bot,
  onUpdate,
  onStart,
  onStop,
  onRestart,
  onDelete,
  disabled,
}: {
  bot: Bot;
  onUpdate: (botId: string, updatedBot: Bot) => void;
  onStart: (botId: string) => void;
  onStop: (botId: string) => void;
  onRestart: (botId: string) => void;
  onDelete: (botId: string) => void;
  disabled?: boolean;
}) {
  const [localBot, setLocalBot] = useState<Bot>(bot);

  // Set up WebSocket connection for this bot
  useWebSocket(bot.id, (update) => {
    const updatedBot = applyBotUpdate(localBot, update);
    setLocalBot(updatedBot);
    onUpdate(bot.id, updatedBot);
  });

  // Update local bot when prop changes
  useEffect(() => {
    setLocalBot(bot);
  }, [bot]);

  return (
    <BotCard
      bot={localBot}
      onStart={onStart}
      onStop={onStop}
      onRestart={onRestart}
      onDelete={onDelete}
    />
  );
}

/**
 * Bot List Component
 *
 * Fetches and displays all bots for the current user with real-time WebSocket updates.
 * Falls back to polling every 30 seconds for data consistency.
 */
export function BotList() {
  const navigate = useNavigate();
  const { bots: initialBots, loading, error, refetch } = useBots(undefined, 30000);
  const [bots, setBots] = useState<Bot[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Update local state when initial bots change
  useEffect(() => {
    setBots(initialBots);
  }, [initialBots]);

  /**
   * Update a single bot in the list
   */
  const updateBot = useCallback((botId: string, updatedBot: Bot) => {
    setBots((prevBots) =>
      prevBots.map((bot) => (bot.id === botId ? updatedBot : bot))
    );
  }, []);

  /**
   * Handle bot start action
   */
  const handleStart = async (botId: string) => {
    setActionLoading(botId);
    setActionError(null);
    try {
      await startBot(botId);
      await refetch();
    } catch (err) {
      setActionError(`Failed to start bot: ${getErrorMessage(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle bot stop action
   */
  const handleStop = async (botId: string) => {
    setActionLoading(botId);
    setActionError(null);
    try {
      await stopBot(botId);
      await refetch();
    } catch (err) {
      setActionError(`Failed to stop bot: ${getErrorMessage(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle bot restart action
   */
  const handleRestart = async (botId: string) => {
    setActionLoading(botId);
    setActionError(null);
    try {
      await restartBot(botId);
      await refetch();
    } catch (err) {
      setActionError(`Failed to restart bot: ${getErrorMessage(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle bot delete action
   */
  const handleDelete = async (botId: string) => {
    if (!confirm("Are you sure you want to delete this bot? This action cannot be undone.")) {
      return;
    }

    setActionLoading(botId);
    setActionError(null);
    try {
      await deleteBot(botId);
      await refetch();
    } catch (err) {
      setActionError(`Failed to delete bot: ${getErrorMessage(err)}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Render loading state
   */
  if (loading && bots.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading bots...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error && bots.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>❌</div>
          <h3 style={styles.errorTitle}>Failed to load bots</h3>
          <p style={styles.errorMessage}>{error}</p>
          <button style={styles.retryButton} onClick={refetch}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (bots.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>🤖</div>
          <h3 style={styles.emptyTitle}>No bots yet</h3>
          <p style={styles.emptyMessage}>
            Get started by creating your first bot instance.
          </p>
          <button style={styles.createButton} onClick={() => navigate("/bots/create")}>
            ＋ Create Your First Bot
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render bot list
   */
  return (
    <div style={styles.container}>
      {/* Action Error Banner */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span style={styles.actionErrorIcon}>⚠️</span>
          <span style={styles.actionErrorText}>{actionError}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setActionError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Bot Grid */}
      <div style={styles.grid}>
        {bots.map((bot) => (
          <div
            key={bot.id}
            style={{
              ...styles.gridItem,
              opacity: actionLoading === bot.id ? 0.6 : 1,
              pointerEvents: actionLoading === bot.id ? "none" : "auto",
            }}
          >
            <BotCardWithWebSocket
              bot={bot}
              onUpdate={updateBot}
              onStart={handleStart}
              onStop={handleStop}
              onRestart={handleRestart}
              onDelete={handleDelete}
              disabled={actionLoading === bot.id}
            />
          </div>
        ))}
      </div>

      {/* Loading Indicator (when polling) */}
      {loading && bots.length > 0 && (
        <div style={styles.pollingIndicator}>
          <span style={styles.pollingIcon}>🔄</span>
          <span style={styles.pollingText}>Updating...</span>
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
  },
  spinner: {
    fontSize: "3rem",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "1rem",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  errorIcon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  errorTitle: {
    margin: "0 0 0.5rem 0",
    fontFamily: PIXEL,
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.87)",
    letterSpacing: "0.06em",
  },
  errorMessage: {
    margin: "0 0 1.5rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    maxWidth: "500px",
  },
  retryButton: {
    padding: "12px 20px",
    background: "transparent",
    border: `2px solid ${BLUE}`,
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: BLUE,
    letterSpacing: "0.1em",
    transition: "all 0.2s",
    boxShadow: `0 0 8px rgba(0,106,167,0.2)`,
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "4rem",
    marginBottom: "1rem",
  },
  emptyTitle: {
    margin: "0 0 0.5rem 0",
    fontFamily: PIXEL,
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.87)",
    letterSpacing: "0.06em",
  },
  emptyMessage: {
    margin: "0 0 1.5rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    maxWidth: "400px",
  },
  createButton: {
    padding: "14px 24px",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "#000",
    letterSpacing: "0.1em",
    transition: "all 0.2s",
    boxShadow: `0 0 12px rgba(254,204,0,0.25)`,
  },
  actionErrorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  actionErrorIcon: {
    fontSize: "1.25rem",
  },
  actionErrorText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "#fca5a5",
  },
  dismissButton: {
    padding: "0.25rem 0.5rem",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    color: "rgba(255,255,255,0.6)",
    transition: "color 0.2s",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: "1.5rem",
  },
  gridItem: {
    transition: "opacity 0.2s",
  },
  pollingIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginTop: "1.5rem",
    padding: "0.75rem",
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.4)",
  },
  pollingIcon: {
    fontSize: "1rem",
    animation: "spin 1s linear infinite",
  },
  pollingText: {
    lineHeight: 1,
  },
};
